// Clipboard helpers. Two variants:
//
//   copyPlain(text)      — writes the raw string.
//   copyRich(md)         — converts markdown to HTML and writes BOTH
//                          text/html and text/plain to the clipboard.
//                          Rich-text-aware targets (Gmail, Slack web,
//                          Notion) render the HTML; terminals + code
//                          editors fall back to the plain string.
//
// Both throw on failure so the caller can surface a "couldn't copy"
// message. Callers should render success/error state, not this module.
import { markdownToHtml } from "./markdown";

export async function copyPlain(text: string): Promise<void> {
  if (!navigator.clipboard?.writeText) {
    throw new Error("clipboard unavailable");
  }
  await navigator.clipboard.writeText(text);
}

/** Copy markdown as rich-text. Pastes-as-formatted into Slack, Gmail,
 *  Notion, etc.; falls back to plain text in editors that don't consume
 *  text/html. The plain fallback is the ORIGINAL markdown, not stripped
 *  HTML — Slack's markdown renderer works on **bold** and _italic_
 *  directly and produces better output than tag-stripped text. */
export async function copyRich(markdown: string): Promise<void> {
  const html = markdownToHtml(markdown);
  // ClipboardItem is the modern API — supported everywhere we run
  // (Chromium, Safari 13.4+, Firefox 127+). Feature-detect and fall
  // back to plain writeText if it's missing.
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    await copyPlain(markdown);
    return;
  }
  const item = new ClipboardItem({
    "text/html": new Blob([html], { type: "text/html" }),
    "text/plain": new Blob([markdown], { type: "text/plain" }),
  });
  await navigator.clipboard.write([item]);
}

/** Copy pre-built HTML as rich text, with an explicit plain-text fallback.
 *  Unlike copyRich (which derives HTML from markdown), the caller supplies
 *  both representations — used by the email/Teams exporters, whose HTML is
 *  hand-built inline-styled markup, not markdown-derived. */
export async function copyHtml(html: string, plain: string): Promise<void> {
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    await copyPlain(plain);
    return;
  }
  const item = new ClipboardItem({
    "text/html": new Blob([html], { type: "text/html" }),
    "text/plain": new Blob([plain], { type: "text/plain" }),
  });
  await navigator.clipboard.write([item]);
}
